import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppLocalStorage } from 'app/app-storage-manager/local-storage';
import { MiscService } from 'app/shared/misc.service';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable, of, Subject, throwError } from 'rxjs';
import { delay, map, retry, tap } from 'rxjs/operators';
import { AgentApiResponse, AssignAgentRequest, KYCApiResponse, KYCFilters, VerifyUserResponse } from './_state/customers.interfaces';

@Injectable({
  providedIn: 'root'
})

export class CustomerService {

    constructor(private http: HttpClient,  private miscService: MiscService) { }
    

    private fetchCustomersSubject = new Subject<void>();

    fetchCustomers$ = this.fetchCustomersSubject.asObservable();

    singleCustomerCreationGoBackClick: BehaviorSubject<any> = new BehaviorSubject('');

    triggerFetchCustomers() {
      this.fetchCustomersSubject.next();
    }

    
    createBulkCustomers = (requestBody: any[]): Observable<any> => {
      return this.http.put(`${environment.CUSTOMER_MANAGEMENT_URL}/services/bulk-insert-data`, requestBody).pipe(retry(3));
    }

    getServiceTypes = () => this.http.get(`${environment.CUSTOMER_MANAGEMENT_URL}/services/get-service-type-by-merchant-id/${this.miscService.getMerchantDetails().merchant_id}`).pipe(retry(3))
    
    createServiceType = (requestBody) => this.http.post(`${environment.CUSTOMER_MANAGEMENT_URL}/services/create-service-type`, requestBody).pipe(retry(3))
    
    updateServiceType = (requestBody, serviceTypeId) => this.http.put(`${environment.CUSTOMER_MANAGEMENT_URL}/services/update-service-type/${serviceTypeId}`, requestBody).pipe(retry(3))
    
    deleteServiceType = (serviceTypeId) => this.http.delete(`${environment.CUSTOMER_MANAGEMENT_URL}/services/delete-service-type/${serviceTypeId}`).pipe(retry(3))
    
    
    getCustomerTypes = () => this.http.get(`${environment.CUSTOMER_MANAGEMENT_URL}/services/get-customer-data-details-by-merchant-id/${this.miscService.getMerchantDetails().merchant_id}`).pipe(retry(3))
    
    createCustomerType = (requestBody) => this.http.post(`${environment.CUSTOMER_MANAGEMENT_URL}/services/create-customer-data-details`, requestBody).pipe(retry(3))
    
    updateCustomerType = (requestBody, customerTypeId) => this.http.put(`${environment.CUSTOMER_MANAGEMENT_URL}/services/update-customer-data-details/${customerTypeId}`, requestBody).pipe(retry(3))

    deleteCustomerType = (customerTypeId) => this.http.delete(`${environment.CUSTOMER_MANAGEMENT_URL}/services/delete-customer-data_details/${customerTypeId}`).pipe(retry(3))


    getCustomersWithoutServiceType = (page, limit, sort = null, search = null, serviceType) => {
      let params = new HttpParams();
      params = params.append('page', page)
      params = params.append('serviceType', serviceType)
      params = params.append('limit', limit)
      params = params.append('sortOrder', 'ASC')
      if(search && sort)
      params = params.append('searchQuery', search)
      params = params.append('sortBy', sort)
      return this.http.get(`${environment.CUSTOMER_MANAGEMENT_URL}/services/retrieve-customers/${this.miscService.getMerchantDetails().currency.iso_code_2}`, {params}).pipe(retry(3))
    }

    getCustomersWithoutServiceTypeSecondVersion = (page, limit, sort = null, search = null, serviceType) => {
      let params = new HttpParams();
      params = params.append('page', page)
      params = params.append('serviceType', serviceType)
      params = params.append('limit', limit)
      params = params.append('sortOrder', 'ASC')
      if(search && sort)
      params = params.append('searchQuery', search)
      params = params.append('sortBy', sort)
      return this.http.get(`${environment.CUSTOMER_MANAGEMENT_URL}/services/retrieve-merchant-customers/${this.miscService.getMerchantDetails().merchant_id}/${this.miscService.getMerchantDetails().currency.iso_code_2}`, {params}).pipe(retry(3))
    }

    createCustomersWithoutServiceType = (payload) => this.http.put(`${environment.CUSTOMER_MANAGEMENT_URL}/services/insert-data`, payload)

    addProductsToCustomer = (payload, customerId) => this.http.put(`${environment.CUSTOMER_MANAGEMENT_URL}/services/add-service/${customerId}`, payload)
    

    getCustomersWithServiceType = (serviceTypeId) => {
      let params = new HttpParams();
      params = params.append('serviceTypeId', serviceTypeId)

      return this.http.get(`${environment.CUSTOMER_MANAGEMENT_URL}/services/retrieve-data/${this.miscService.getMerchantDetails().merchant_id}`, {params}).pipe(retry(3))
    }

    createCustomerSingleWithServiceType = (requestBody, customerTypeId) => this.http.put(`${environment.CUSTOMER_MANAGEMENT_URL}/services/insert-data/${customerTypeId}`, requestBody).pipe(retry(3))

    createCustomerBulkWithServiceType = (requestBody, customerTypeId) => this.http.put(`${environment.CUSTOMER_MANAGEMENT_URL}/services/bulk-insert-data/${customerTypeId}`, requestBody).pipe(retry(3))

    getCustomerCSVTemplate = () => this.http.get(`${environment.CUSTOMER_MANAGEMENT_URL}/services/data-template`, {responseType: 'blob'}).pipe(retry(3))


  getKYCRecords(filters: KYCFilters = {}): Observable<KYCApiResponse> {
    let params = new HttpParams();

    if (filters.page) {
      params = params.set('page', filters.page);
    }
    
    if (filters.pageSize) {
      params = params.set('pageSize', filters.pageSize);
    }
    if (filters.search) {
      params = params.set('search', filters.search);
    }
    
    if (filters.userid !== undefined) {
      params = params.set('userid', filters.userid.toString());
    }
    
    if (filters.issuerCode) {
      params = params.set('issuerCode', filters.issuerCode);
    }
    
    if (filters.subIssuerCode) {
      params = params.set('subIssuerCode', filters.subIssuerCode);
    }
    
    if (filters.mno) {
      params = params.set('mno', filters.mno);
    }
    
    if (filters.simRegistrationStatus) {
      params = params.set('simRegistrationStatus', filters.simRegistrationStatus);
    }
    
    if (filters.startDate) {
      params = params.set('startDate', filters.startDate);
    }
    
    if (filters.endDate) {
      params = params.set('endDate', filters.endDate);
    }
    
    if (filters.is_business !== undefined) {
      params = params.set('is_business', filters.is_business.toString());
    }
    
    if (filters.openTo) {
      params = params.set('openTo', filters.openTo);
    }

    return this.http.get<KYCApiResponse>(`${environment.API_URL2}/user-service/verification/user-data`, { params });
  }

  exportKYCRecordsCSV(filters: KYCFilters = {}): Observable<Blob> {
    let params = new HttpParams();
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
        params = params.set(key, filters[key].toString());
      }
    });
    
    params = params.set('format', 'csv');

    return this.http.get(`${environment.API_URL2}/user-service`, { 
      params, 
      responseType: 'blob' 
    });
  }

  //   getAgents(filters: any = {}): Observable<AgentApiResponse> {
  //   let params = new HttpParams();
    
  //   // Required parameter
  //   if (filters.merchant_id) {
  //     params = params.set('merchant_id', filters.merchant_id);
  //   }
    
  //   // Always include status and search (even if empty)
  //   params = params.set('status', filters.status || '');
  //   params = params.set('search', filters.search || '');
    
  //   // Optional pagination parameters
  //   if (filters.page) {
  //     params = params.set('page', filters.page);
  //   }
    
  //   if (filters.pageSize) {
  //     params = params.set('pageSize', filters.pageSize);
  //   }

  //   return this.http.get<AgentApiResponse>(`${this.baseUrl}/association/xcel/all-agent`, { params });
  // }


  getAgents(filters: any = {}): Observable<AgentApiResponse> {
    let params = new HttpParams();

    if (filters.merchant_id) {
      params = params.set('merchant_id', filters.merchant_id);
    }

    if (filters.page) {
      params = params.set('page', filters.page);
    }
    
    if (filters.pageSize) {
      params = params.set('pageSize', filters.pageSize);
    }

    return this.http.get<AgentApiResponse>(`${environment.API_URL2}/user-service/association/xcel/all-agent`, { params });
  }

  assignAsAgent(request: AssignAgentRequest): Observable<any> {
    return this.http.post(`${environment.API_URL2}/user-service/association/assign-agent`, request);
  }

  removeAgent(agentId: string): Observable<any> {
    return this.http.delete(`${environment.API_URL2}/user-service/association/xcel/agent/${agentId}`);
  }

  updateAgentRole(agentId: string, role: string): Observable<any> {
    return this.http.patch(`${environment.API_URL2}/user-service/association/agent/${agentId}`, { role });
  }
}
